import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, designOptionsAPI, materialsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Upload,
  Plus,
  Trash2,
  FileText,
  Building2,
  Leaf,
  MapPin,
  Layers,
  Scale,
  Target,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOptionDialogOpen, setIsOptionDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedMaterials, setUploadedMaterials] = useState([]);

  const [optionForm, setOptionForm] = useState({
    name: '',
    description: '',
    materials: [{ material_id: '', quantity: '', unit: 'kg' }],
  });

  const COLORS = ['#0F2818', '#800020', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899'];

  const fetchProject = useCallback(async () => {
    try {
      const [projectRes, materialsRes] = await Promise.all([
        projectsAPI.getOne(id),
        materialsAPI.getAll(),
      ]);
      setProject(projectRes.data);
      setMaterials(materialsRes.data.materials);
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const handleAddMaterial = () => {
    setOptionForm({
      ...optionForm,
      materials: [...optionForm.materials, { material_id: '', quantity: '', unit: 'kg' }],
    });
  };

  const handleRemoveMaterial = (index) => {
    setOptionForm({
      ...optionForm,
      materials: optionForm.materials.filter((_, i) => i !== index),
    });
  };

  const handleMaterialChange = (index, field, value) => {
    const newMaterials = [...optionForm.materials];
    newMaterials[index][field] = value;
    setOptionForm({ ...optionForm, materials: newMaterials });
  };

  const handleCreateOption = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        name: optionForm.name,
        description: optionForm.description,
        materials: optionForm.materials
          .filter((m) => m.material_id && m.quantity)
          .map((m) => ({
            material_id: m.material_id,
            quantity: parseFloat(m.quantity),
            unit: m.unit,
          })),
      };

      if (payload.materials.length === 0) {
        toast.error('Please add at least one material');
        setCreating(false);
        return;
      }

      await designOptionsAPI.create(id, payload);
      toast.success('Design option created');
      setIsOptionDialogOpen(false);
      setOptionForm({
        name: '',
        description: '',
        materials: [{ material_id: '', quantity: '', unit: 'kg' }],
      });
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create option');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteOption = async (optionId) => {
    if (!window.confirm('Delete this design option?')) return;
    try {
      await designOptionsAPI.delete(id, optionId);
      toast.success('Design option deleted');
      fetchProject();
    } catch (error) {
      toast.error('Failed to delete option');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await projectsAPI.uploadIFC(id, file);
      setUploadedMaterials(response.data.materials);
      toast.success(`Extracted ${response.data.materials_extracted} materials from IFC file`);
    } catch (error) {
      toast.error('Failed to process IFC file');
    } finally {
      setUploading(false);
    }
  };

  const handleUseExtractedMaterials = () => {
    setOptionForm({
      ...optionForm,
      materials: uploadedMaterials.map((m) => ({
        material_id: m.material_id,
        quantity: m.quantity.toString(),
        unit: m.unit,
      })),
    });
    setIsUploadDialogOpen(false);
    setIsOptionDialogOpen(true);
    toast.success('Materials loaded from IFC file');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-[#0F2818] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) return null;

  // Calculate total carbon
  const totalCarbon = project.design_options?.reduce((acc, opt) => acc + (opt.total_carbon || 0), 0) || 0;
  const budgetStatus = project.carbon_budget
    ? totalCarbon <= project.carbon_budget
      ? 'within'
      : 'over'
    : null;

  // Chart data for best design option
  const bestOption = project.design_options?.length
    ? project.design_options.reduce((best, opt) =>
        opt.total_carbon < best.total_carbon ? opt : best
      )
    : null;

  const categoryData = bestOption
    ? Object.entries(bestOption.carbon_by_category || {}).map(([name, value]) => ({
        name,
        value: Math.round(value),
      }))
    : [];

  return (
    <div className="p-8" data-testid="project-detail-page">
      {/* Header */}
      <div className="page-header -mx-8 -mt-8 mb-8 px-8 py-6 bg-white border-b border-slate-200">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate('/projects')}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {project.name}
            </h1>
            {project.description && (
              <p className="text-slate-600 mt-1">{project.description}</p>
            )}
          </div>
          <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="upload-ifc-button">
                <Upload className="w-4 h-4 mr-2" />
                Upload IFC
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Upload IFC File
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="file-upload-zone" onClick={() => document.getElementById('ifc-file').click()}>
                  <input
                    type="file"
                    id="ifc-file"
                    accept=".ifc"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="ifc-file-input"
                  />
                  <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                  <p className="text-slate-600">
                    {uploading ? 'Processing...' : 'Click to upload IFC file'}
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    Supports .ifc files from Revit and other BIM software
                  </p>
                </div>

                {uploadedMaterials.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-slate-900">Extracted Materials:</h4>
                    <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Material</TableHead>
                            <TableHead className="text-right">Quantity</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {uploadedMaterials.map((mat, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{mat.name}</TableCell>
                              <TableCell className="text-right font-mono">
                                {mat.quantity} {mat.unit}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <Button
                      className="w-full bg-[#0F2818] hover:bg-[#06140C]"
                      onClick={handleUseExtractedMaterials}
                      data-testid="use-extracted-materials"
                    >
                      Use These Materials
                    </Button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={isOptionDialogOpen} onOpenChange={setIsOptionDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F2818] hover:bg-[#06140C]" data-testid="add-option-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Design Option
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Create Design Option
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateOption} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="option-name">Option Name *</Label>
                    <Input
                      id="option-name"
                      value={optionForm.name}
                      onChange={(e) => setOptionForm({ ...optionForm, name: e.target.value })}
                      placeholder="e.g., Option A - Steel Frame"
                      required
                      data-testid="option-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="option-desc">Description</Label>
                    <Input
                      id="option-desc"
                      value={optionForm.description}
                      onChange={(e) => setOptionForm({ ...optionForm, description: e.target.value })}
                      placeholder="Brief description"
                      data-testid="option-description-input"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Materials</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddMaterial}
                      data-testid="add-material-button"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Material
                    </Button>
                  </div>

                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {optionForm.materials.map((mat, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <Select
                          value={mat.material_id}
                          onValueChange={(value) => handleMaterialChange(index, 'material_id', value)}
                        >
                          <SelectTrigger className="flex-1" data-testid={`material-select-${index}`}>
                            <SelectValue placeholder="Select material" />
                          </SelectTrigger>
                          <SelectContent>
                            {materials.map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.name} ({m.carbon_factor} {m.unit})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          value={mat.quantity}
                          onChange={(e) => handleMaterialChange(index, 'quantity', e.target.value)}
                          placeholder="Qty"
                          className="w-24"
                          data-testid={`material-quantity-${index}`}
                        />
                        <span className="text-sm text-slate-500 w-8">kg</span>
                        {optionForm.materials.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveMaterial(index)}
                            className="text-slate-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsOptionDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#0F2818] hover:bg-[#06140C]"
                    disabled={creating}
                    data-testid="submit-option-button"
                  >
                    {creating ? 'Creating...' : 'Create Option'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Project Info Pills */}
        <div className="flex flex-wrap gap-4">
          {project.location && (
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full">
              <MapPin className="w-4 h-4" />
              {project.location}
            </div>
          )}
          {project.building_type && (
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full">
              <Layers className="w-4 h-4" />
              {project.building_type}
            </div>
          )}
          {project.gross_floor_area && (
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full">
              <Scale className="w-4 h-4" />
              {project.gross_floor_area.toLocaleString()} m²
            </div>
          )}
          {project.carbon_budget && (
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full">
              <Target className="w-4 h-4" />
              Budget: {(project.carbon_budget / 1000).toFixed(1)} tCO2e
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="border-slate-200" data-testid="total-carbon-stat">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#E8EEEA] flex items-center justify-center">
                <Leaf className="w-6 h-6 text-[#0F2818]" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Embodied Carbon</p>
                <p className="text-2xl font-bold text-slate-900">
                  {(totalCarbon / 1000).toFixed(2)}
                  <span className="text-sm font-normal text-slate-500 ml-1">tCO2e</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="design-options-stat">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#3B82F6]" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Design Options</p>
                <p className="text-2xl font-bold text-slate-900">
                  {project.design_options?.length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="budget-status-stat">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                budgetStatus === 'within' ? 'bg-[#D6E0DA]' : budgetStatus === 'over' ? 'bg-[#FEE2E2]' : 'bg-slate-100'
              }`}>
                <Target className={`w-6 h-6 ${
                  budgetStatus === 'within' ? 'text-green-600' : budgetStatus === 'over' ? 'text-red-600' : 'text-slate-500'
                }`} />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Budget Status</p>
                <p className="text-lg font-semibold">
                  {budgetStatus === 'within' ? (
                    <span className="text-green-600">Within Budget</span>
                  ) : budgetStatus === 'over' ? (
                    <span className="text-red-600">Over Budget</span>
                  ) : (
                    <span className="text-slate-500">No Budget Set</span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Design Options */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Options List */}
        <Card className="border-slate-200" data-testid="design-options-list">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Design Options
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {project.design_options?.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {project.design_options.map((option, index) => {
                  const isLowestCarbon = project.design_options.length > 1 && 
                    option.total_carbon === Math.min(...project.design_options.map(o => o.total_carbon));
                  return (
                  <div
                    key={option.id}
                    className="p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`design-option-${option.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-slate-900">{option.name}</h4>
                          {isLowestCarbon && (
                            <Badge className="bg-[#D6E0DA] text-green-700 hover:bg-[#D6E0DA]">
                              Lowest Carbon
                            </Badge>
                          )}
                        </div>
                        {option.description && (
                          <p className="text-sm text-slate-600 mt-1">{option.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-sm text-slate-500">
                            {option.materials?.length || 0} materials
                          </span>
                          <span className="font-mono text-sm font-medium text-[#0F2818]">
                            {(option.total_carbon / 1000).toFixed(2)} tCO2e
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteOption(option.id)}
                        className="text-slate-400 hover:text-red-500"
                        data-testid={`delete-option-${option.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mb-4 text-slate-300" />
                <p className="mb-2">No design options yet</p>
                <p className="text-sm">Upload an IFC file or add materials manually</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Carbon Breakdown Chart */}
        <Card className="border-slate-200" data-testid="carbon-breakdown-chart">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Carbon by Category {bestOption ? `(${bestOption.name})` : ''}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {categoryData.length > 0 ? (
              <div className="flex items-center">
                <ResponsiveContainer width="50%" height={240}>
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      dataKey="value"
                      paddingAngle={2}
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`${(value / 1000).toFixed(2)} tCO2e`, 'Carbon']}
                      contentStyle={{ borderRadius: 6, border: '1px solid #E2E8F0' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="w-1/2 pl-4">
                  <ul className="space-y-2">
                    {categoryData.map((item, index) => (
                      <li key={item.name} className="flex items-center gap-2 text-sm">
                        <span
                          className="w-3 h-3 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-slate-600 truncate">{item.name}</span>
                        <span className="ml-auto font-mono text-slate-900">
                          {(item.value / 1000).toFixed(2)}t
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-60 text-slate-500">
                <Leaf className="w-12 h-12 mb-4 text-slate-300" />
                <p>Add design options to see carbon breakdown</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProjectDetail;
